/* =====================================================================================
   What each kind of record can be searched on.

   Field catalogues, not a hardcoded form. Adding a searchable attribute is a line here rather
   than a change to the search screen, and the same catalogue drives the criteria builder, the
   operators offered and the result columns.

   TENANT-DERIVED, NOT TRANSPLANT-SHAPED. The per-segment volume fields are generated from
   TENANT.vocabulary.segment, so the transplant tenant gets "Kidney / year" and the orthopaedic
   one gets "Knee, primary / year" from the same code. Writing them out by hand would have put
   organs back into shared code, which is the mistake this product has already made three times.
   ===================================================================================== */

import { TENANT } from '@tenant';
import { siteTotal } from '../data/atlas.core';
import type { Site } from '../data/atlas';
import type { SearchField } from './search';

/** A site enriched with whatever the database could add. Seed fields always; the rest if live. */
export interface SearchableSite extends Record<string, unknown> {
  id: string; name: string; short: string; city: string; region: string; iso3: string;
  tier: number; kinds: string[]; volumes: { organ: string; total: number }[];
  prevalentGrafts?: { value: number } | null;
  waitingList?: number | null;
  incumbent?: string[] | null;
  note?: string | null;
  paediatric?: boolean;
  geo?: string | null;
  /* Enrichment from atlas.v_technical_readiness and atlas.v_site_addressable. Undefined rather
     than false when the site has no technical record, because "we have not asked" and "they do
     not have one" are different answers and the search must be able to tell them apart. */
  hasLongRead?: boolean | null;
  sequencers?: string[] | null;
  hlaMethods?: string[] | null;
  accreditations?: string[] | null;
  annualTypings?: number | null;
  sendOutTests?: string[] | null;
  readiness?: string | null;
  addressableValue?: number | null;
  /* How much is known about this site, and what is not. From atlas.v_site_completeness. */
  completeness?: number;
  missing?: string[];
}

const countryName = (iso: string) =>
  (TENANT.seed.country as { iso3: string; name: string }[]).find((c) => c.iso3 === iso)?.name ?? iso;

/* COLOCATION IS DERIVED, BECAUSE THE MODEL CANNOT STATE IT DIRECTLY.

   A laboratory is its own site record, not a property of the clinical unit it serves:
   Addenbrooke's Hospital and the Tissue Typing Laboratory at Addenbrooke's are two rows. So
   "a centre with a colocated laboratory", which is one of the most commercially useful things
   anyone can ask, cannot be written as a filter on the centre's own fields. Asking for it
   directly returns the laboratories themselves, which have no transplant volume and are not
   what the question meant.

   Colocation is therefore computed: a centre counts as having a laboratory alongside it when
   another site in the same city and country carries a laboratory kind. City is the right grain.
   Same country is far too loose, and a shared parent record is too strict because the seed
   frequently holds the laboratory under a different trust or university name entirely. */
/* Places recorded under a name that is not the name of the city they are legally part of.

   DELIBERATELY TINY, and it must stay that way. The New York boroughs are one city, so a
   laboratory in Brooklyn IS in the same city as a hospital in Manhattan and the model was simply
   wrong to say otherwise: NYU Langone's Immunogenetics Laboratory is at 882 Third Avenue,
   Brooklyn, serving a transplant programme at Tisch Hospital in Manhattan, and before this it was
   the one centre in the model whose own laboratory did not count as its own laboratory.

   The temptation is to extend this to metropolitan areas, and it must be resisted. Dallas and
   Fort Worth are not one city, and a send-out between them is exactly the friction the search is
   meant to find. This maps administrative identity only, never travel time. Anything looser turns
   "has a laboratory in the same city" into "has a laboratory somewhere in the region", which is a
   different question with a much less useful answer. */
const SAME_CITY: Record<string, string> = {
  'USA|brooklyn': 'new york',
  'USA|bronx': 'new york',
  'USA|queens': 'new york',
  'USA|staten island': 'new york',
  'USA|manhattan': 'new york',
};

function colocationIndex(sites: SearchableSite[]) {
  const labKinds = new Set(
    TENANT.vocabulary.siteKinds.filter((k) => /lab/i.test(k.label)).map((k) => k.key));
  if (!labKinds.size) return null;

  const cityKey = (s: SearchableSite) => {
    const raw = `${s.iso3}|${(s.city ?? '').trim().toLowerCase()}`;
    return SAME_CITY[raw] ? `${s.iso3}|${SAME_CITY[raw]}` : raw;
  };

  const labsByCity = new Map<string, string[]>();
  for (const s of sites) {
    if (!s.kinds.some((k) => labKinds.has(k))) continue;
    labsByCity.set(cityKey(s), [...(labsByCity.get(cityKey(s)) ?? []), s.short || s.name]);
  }
  return {
    labKinds,
    /** Laboratories in the same city, excluding the site itself when it IS one. */
    near: (s: SearchableSite) => {
      const own = s.short || s.name;
      return (labsByCity.get(cityKey(s)) ?? []).filter((n) => n !== own);
    },
  };
}

export function siteFields(sites: SearchableSite[]): SearchField<SearchableSite>[] {
  const seg = TENANT.vocabulary.segment;
  const colo = colocationIndex(sites);
  /* Sorted by the NAME shown, not the code stored. Sorting the ISO codes and then rendering
     their names produced a list that looked alphabetical and was not: ALB, ARE, ARG put the
     United Arab Emirates second, between Albania and Argentina. localeCompare so accented names
     file where a reader expects rather than after Z. */
  const countries = [...new Set(sites.map((s) => s.iso3))]
    .map((iso) => ({ value: iso, label: countryName(iso) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'en'));

  /* Volume bands are suggested from the data rather than guessed, so "mid-size" means mid-size
     in THIS market. A hardcoded 50 to 200 is right for kidney units and absurd for arthroplasty
     hospitals doing four thousand joints a year. */
  const totals = sites.map((s) => siteTotal(s as unknown as Site)).filter((n) => n > 0).sort((a, b) => a - b);
  const q = (p: number) => totals.length ? totals[Math.floor((totals.length - 1) * p)] : 0;

  const fields: SearchField<SearchableSite>[] = [
    { key: 'name', label: 'Name', type: 'text', get: (s) => `${s.name} ${s.short}` },
    { key: 'city', label: 'City', type: 'text', get: (s) => s.city },
    { key: 'region', label: 'Region or state', type: 'text', get: (s) => s.region },
    { key: 'iso3', label: 'Country', type: 'enum', get: (s) => s.iso3, options: countries },
    { key: 'tier', label: 'Tier', type: 'enum', get: (s) => String(s.tier),
      options: [{ value: '1', label: 'Tier 1' }, { value: '2', label: 'Tier 2' },
                { value: '3', label: 'Tier 3' }],
      hint: 'Tier 1 decides the market: highest volume, or academically dominant.' },
    { key: 'kinds', label: `What the ${TENANT.vocabulary.site.toLowerCase()} is`, type: 'array',
      get: (s) => s.kinds,
      options: TENANT.vocabulary.siteKinds.map((k) => ({
        value: k.key, label: k.label === '@site' ? TENANT.vocabulary.site : k.label })),
      hint: 'Use this to require a colocated laboratory rather than just a clinical unit.' },
    { key: 'total', label: `Total ${TENANT.vocabulary.volumeLabel.toLowerCase()}`, type: 'number',
      get: (s) => siteTotal(s as unknown as Site),
      suggest: [q(0.35), q(0.8)],
      hint: 'A range scores best in the middle and tails off outside it, so "mid-size" ranks '
          + 'rather than excludes.' },
    { key: 'prevalent', label: TENANT.vocabulary.population, type: 'number',
      get: (s) => s.prevalentGrafts?.value ?? null },
    { key: 'waiting', label: 'Waiting list', type: 'number', get: (s) => s.waitingList ?? null },
    { key: 'incumbent', label: 'Incumbent supplier', type: 'array',
      get: (s) => s.incumbent ?? [],
      hint: 'Who they already buy from, where the field has recorded it.' },
    { key: 'note', label: 'Commercial assessment', type: 'text', get: (s) => s.note ?? '' },
  ];

  if (colo) {
    fields.push(
      { key: 'colocatedLab', label: 'Has a laboratory in the same city', type: 'boolean',
        get: (s) => String(colo.near(s).length > 0),
        options: [{ value: 'true', label: 'yes' }, { value: 'false', label: 'no' }],
        hint: 'Derived. A laboratory is a separate record from the clinical unit it serves, so '
            + 'asking for a laboratory KIND returns the laboratories themselves rather than the '
            + 'centres that have one nearby. This asks the question people actually mean.' },
      { key: 'colocatedLabs', label: 'Laboratories in the same city', type: 'array',
        get: (s) => colo.near(s) },
      { key: 'isLab', label: 'Is itself a laboratory', type: 'boolean',
        get: (s) => String(s.kinds.some((k) => colo.labKinds.has(k))),
        options: [{ value: 'true', label: 'yes' }, { value: 'false', label: 'no' }],
        hint: 'Set this to no to keep standalone laboratories out of a list of clinical units.' },
    );
  }

  if (seg) {
    for (const k of seg.keys) {
      fields.push({
        key: `vol_${k}`, label: `${seg.labels[k] ?? k} / year`, type: 'number',
        get: (s) => s.volumes.find((v) => v.organ === k)?.total ?? null,
      });
    }
  }

  /* Technical enrichment. Only offered when something has actually been recorded, because a
     filter over a column that is empty everywhere returns nothing and looks broken. */
  const anyTechnical = sites.some((s) => s.readiness != null || s.sequencers?.length
                                      || s.sendOutTests?.length || s.annualTypings != null);
  if (anyTechnical) {
    fields.push(
      { key: 'sendOut', label: 'Tests they send out', type: 'array', get: (s) => s.sendOutTests ?? [],
        hint: 'A laboratory that already sends work away is paying somebody for it and is '
            + 'usually unhappy about the turnaround.' },
      { key: 'hasLongRead', label: 'Has a long-read sequencer', type: 'boolean',
        get: (s) => (s.hasLongRead == null ? null : String(s.hasLongRead)),
        options: [{ value: 'true', label: 'yes' }, { value: 'false', label: 'no' }],
        hint: 'No long-read platform means building the assay in-house is not a near-term option '
            + 'for them, however much volume they have.' },
      { key: 'sequencers', label: 'Sequencers', type: 'array', get: (s) => s.sequencers ?? [] },
      { key: 'hlaMethods', label: 'Typing methods', type: 'array', get: (s) => s.hlaMethods ?? [] },
      { key: 'accreditations', label: 'Accreditations', type: 'array', get: (s) => s.accreditations ?? [] },
      { key: 'annualTypings', label: 'Annual typings', type: 'number',
        get: (s) => s.annualTypings ?? null },
      { key: 'readiness', label: 'Technical readiness', type: 'enum',
        get: (s) => s.readiness ?? null,
        options: [{ value: 'ready', label: 'ready' }, { value: 'partial', label: 'partial' },
                  { value: 'not_ready', label: 'not ready' }] },
    );
  }
  /* Offered only when the enrichment loaded, because a filter over a column that is empty
     everywhere returns nothing and looks broken rather than empty. */
  if (sites.some((s) => s.completeness != null)) {
    fields.push(
      { key: 'completeness', label: 'How much we know', type: 'number',
        get: (s) => s.completeness ?? null,
        suggest: [0, 40],
        hint: 'Percentage of the facts that decide a deal. Prefer a LOW value to find the '
            + 'targets worth researching, or a high one to find the ones ready to approach.' },
      { key: 'missing', label: 'What we do not know', type: 'array',
        get: (s) => s.missing ?? [],
        options: [
          { value: 'who supplies them', label: 'Who supplies them' },
          { value: 'what they send out', label: 'What they send out' },
          { value: 'platform', label: 'Platform' },
          { value: 'a named contact', label: 'A named contact' },
          { value: 'turnaround', label: 'Turnaround' },
          { value: 'accreditation', label: 'Accreditation' },
          { value: 'activity', label: 'Activity' },
          { value: 'website', label: 'Website' },
        ],
        hint: 'Use this to build a research list: the centres worth calling that we still '
            + 'cannot describe.' },
    );
  }

  if (sites.some((s) => s.addressableValue != null)) {
    fields.push({ key: 'addressable', label: 'Addressable annual value', type: 'number',
                  get: (s) => s.addressableValue ?? null });
  }

  return fields;
}

/* ---------------------------------------------------------------------------------
   Saved shapes of question, because the useful ones are asked repeatedly and nobody
   should have to rebuild a seven-criterion search from memory each time.
   --------------------------------------------------------------------------------- */

export interface Preset {
  key: string; name: string; note: string;
  build: (fields: SearchField<SearchableSite>[]) =>
    { field: string; op: string; value?: unknown; mode: 'must' | 'prefer'; weight?: number }[];
}

export const SITE_PRESETS: Preset[] = [
  {
    key: 'midvolume-sendout',
    name: 'Mid-volume, sending work out, cannot insource',
    note: 'The classic service-sale target: enough volume to matter, already paying somebody '
        + 'else, and without the platform to bring it in-house.',
    build: (fields) => {
      const total = fields.find((f) => f.key === 'total');
      const band = total?.suggest ?? [50, 200];
      const out: ReturnType<Preset['build']> = [
        { field: 'total', op: 'between', value: band, mode: 'prefer', weight: 2 },
      ];
      if (fields.some((f) => f.key === 'sendOut')) {
        out.push({ field: 'sendOut', op: 'present', mode: 'prefer', weight: 2 });
      }
      if (fields.some((f) => f.key === 'hasLongRead')) {
        out.push({ field: 'hasLongRead', op: 'is', value: 'false', mode: 'prefer', weight: 1.5 });
      }
      return out;
    },
  },
  {
    key: 'colocated-lab',
    name: 'Clinical units with a laboratory in the same city',
    note: 'A unit whose laboratory is on the doorstep decides and buys in one place. Excludes '
        + 'the laboratories themselves, which are separate records and have no clinical volume.',
    build: (fields) => {
      if (!fields.some((f) => f.key === 'colocatedLab')) return [];
      return [
        { field: 'colocatedLab', op: 'is', value: 'true', mode: 'must' },
        { field: 'isLab', op: 'is', value: 'false', mode: 'must' },
      ];
    },
  },
  {
    key: 'displaceable',
    name: 'Someone else is already there',
    note: 'An incumbent is recorded, so there is a contract to displace and a date to find.',
    build: () => [{ field: 'incumbent', op: 'present', mode: 'must' }],
  },
  {
    key: 'top-tier',
    name: 'The centres that decide the market',
    note: 'Tier 1 by volume or academic weight, ranked by size.',
    build: () => [
      { field: 'tier', op: 'is', value: '1', mode: 'must' },
      { field: 'total', op: 'gte', value: 0, mode: 'prefer', weight: 1 },
    ],
  },
];


/* =====================================================================================
   Competitors, for Circus. The same engine, a different catalogue.

   Proof that the search is generic rather than an Atlas feature that happens to be reusable:
   nothing below knows anything about sites, and nothing in SearchBuilder knows anything about
   either. A third catalogue is a third export from this file.
   ===================================================================================== */

export interface SearchableCompetitor extends Record<string, unknown> {
  id: string; name: string; short_name?: string | null; parent_name?: string | null;
  country?: string | null; model: string; threat: string;
  ownership?: string | null; scale?: string | null;
  their_strength?: string | null; their_weakness?: string | null;
  our_position?: string | null; notes?: string | null;
  aliases?: string[] | null;
  source_name: string; confidence: string;
  offeringCount?: number; categories?: string[];
}

export function competitorFields(): SearchField<SearchableCompetitor>[] {
  return [
    { key: 'name', label: 'Name', type: 'text',
      get: (c) => `${c.name} ${c.short_name ?? ''} ${c.parent_name ?? ''} ${(c.aliases ?? []).join(' ')}`,
      hint: 'Searches trading names and aliases too, so GenDx finds Eurobio Scientific.' },
    { key: 'threat', label: 'Threat', type: 'enum', get: (c) => c.threat,
      options: ['primary', 'significant', 'watch', 'peripheral', 'unknown']
        .map((v) => ({ value: v, label: v })) },
    { key: 'model', label: 'What they sell', type: 'enum', get: (c) => c.model,
      options: ['kit', 'service', 'both', 'platform', 'distributor', 'unknown']
        .map((v) => ({ value: v, label: v })),
      hint: 'A kit is bought by a laboratory that runs it; a service means samples leave the '
          + 'building. Different buyer, different budget, different sale.' },
    { key: 'scale', label: 'Scale', type: 'enum', get: (c) => c.scale ?? null,
      options: ['global', 'regional', 'national', 'niche', 'unknown']
        .map((v) => ({ value: v, label: v })) },
    { key: 'country', label: 'Headquarters', type: 'text', get: (c) => c.country ?? '' },
    { key: 'categories', label: 'Product categories', type: 'array', get: (c) => c.categories ?? [] },
    { key: 'offerings', label: 'Products recorded', type: 'number', get: (c) => c.offeringCount ?? 0 },
    { key: 'weakness', label: 'Their weakness', type: 'text', get: (c) => c.their_weakness ?? '',
      hint: 'Free text. Search it for the thing you want to attack: "Luminex", "cash", '
          + '"North American".' },
    { key: 'strength', label: 'Their strength', type: 'text', get: (c) => c.their_strength ?? '' },
    { key: 'position', label: 'Where we stand', type: 'text', get: (c) => c.our_position ?? '' },
    { key: 'confidence', label: 'Confidence', type: 'enum', get: (c) => c.confidence,
      options: ['verified', 'derived', 'estimated', 'unverified'].map((v) => ({ value: v, label: v })) },
  ];
}

export const COMPETITOR_PRESETS = [
  {
    key: 'primary-service',
    name: 'Primary threats selling a service',
    note: 'The ones taking samples out of a laboratory, which is the sale to displace.',
    build: () => [
      { field: 'threat', op: 'is', value: 'primary', mode: 'must' as const },
      { field: 'model', op: 'in', value: ['service', 'both'], mode: 'must' as const },
    ],
  },
  {
    key: 'vulnerable',
    name: 'Weakness recorded, ranked by threat',
    note: 'Somebody has written down where they are soft. Start there.',
    build: () => [
      { field: 'weakness', op: 'present', mode: 'must' as const },
      { field: 'threat', op: 'is', value: 'primary', mode: 'prefer' as const, weight: 2 },
    ],
  },
  {
    key: 'thin-evidence',
    name: 'Held on thin evidence',
    note: 'Records that are estimated or unverified, and are due a check.',
    build: () => [
      { field: 'confidence', op: 'in', value: ['estimated', 'unverified'], mode: 'must' as const },
    ],
  },
];
